import { TIME_RANGE_OPTIONS } from '@/config/dashboardOptions'

// Search-specific options: "All Time" default + all presets (excluding dashboard "All Time" which has value 'all')
export const SEARCH_TIME_RANGE_OPTIONS = [
  { value: null, label: 'All Time' },
  ...TIME_RANGE_OPTIONS.filter(o => o.value !== 'all')
]

// Convert preset string to milliseconds duration
export function presetToMs(preset) {
  const map = {
    '1h': 3600000,
    '6h': 21600000,
    '24h': 86400000,
    '7d': 604800000,
    '30d': 2592000000,
    '180d': 15552000000,
    '365d': 31536000000,
  }
  return map[preset] || null
}

// Convert a time range selection to { start, end } in ms (or null)
export function selectionToFilterParams(selection) {
  if (!selection) return null
  if (selection.type === 'preset') {
    const ms = presetToMs(selection.value)
    if (!ms) return null
    return { start: Date.now() - ms, end: null }
  }
  if (selection.type === 'custom') {
    return { start: selection.start, end: selection.end }
  }
  return null
}

// Extract all ts:[start TO end] clauses from a query string.
// Multiple clauses are merged as an intersection (tightest range).
export function extractTimeFromQuery(queryString) {
  const noMatch = { cleanQuery: queryString, start: null, end: null }
  if (!queryString) return noMatch

  const tsRegex = /\(?ts:\[(\d+)\s+TO\s+(\d+|\*)\]\)?/gi
  const matches = [...queryString.matchAll(tsRegex)]
  if (matches.length === 0) return noMatch

  // Merge: latest start, earliest end (intersection of all ranges)
  let start = -Infinity
  let end = null
  for (const m of matches) {
    start = Math.max(start, parseInt(m[1]))
    if (m[2] !== '*') {
      const e = parseInt(m[2])
      end = end === null ? e : Math.min(end, e)
    }
  }

  // Remove all ts clauses and clean up dangling/doubled operators
  const cleanQuery = queryString
    .replace(tsRegex, '')
    .replace(/\s+/g, ' ')
    .replace(/(?:AND|OR)\s+(?:AND|OR)/gi, 'AND')
    .replace(/^\s*(?:AND|OR)\s+/i, '')
    .replace(/\s+(?:AND|OR)\s*$/i, '')
    .trim()

  return { cleanQuery, start, end }
}

// Format a raw ms time range into a human-readable label.
// Tries to match known presets first, otherwise falls back to date range format.
export function formatTimeRange(start, end) {
  if (start == null && end == null) return 'All Time'

  const now = Date.now()
  const TOLERANCE = 120_000 // 2 min tolerance for preset matching

  // Try preset matching: if end is null/close to now, check if (now - start) matches a preset
  if (start != null && (end == null || Math.abs(end - now) < TOLERANCE)) {
    const delta = (end || now) - start
    const presets = [
      { ms: 3600000, label: 'Last Hour' },
      { ms: 21600000, label: 'Last 6 Hours' },
      { ms: 86400000, label: 'Last 24 Hours' },
      { ms: 604800000, label: 'Last 7 Days' },
      { ms: 2592000000, label: 'Last 30 Days' },
      { ms: 15552000000, label: 'Last 6 Months' },
      { ms: 31536000000, label: 'Last 12 Months' },
    ]
    for (const p of presets) {
      if (Math.abs(delta - p.ms) / p.ms < 0.05) return p.label
    }
  }

  // Fallback to date range format
  const fmt = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (start != null && end != null) return `${fmt(start)} — ${fmt(end)}`
  if (start != null) return `Since ${fmt(start)}`
  return `Before ${fmt(end)}`
}

// Format display label for a selection
export function formatSelectionLabel(selection) {
  if (!selection) return 'All Time'
  if (selection.type === 'preset') {
    const opt = SEARCH_TIME_RANGE_OPTIONS.find(o => o.value === selection.value)
    return opt?.label || selection.value
  }
  if (selection.type === 'custom') {
    const fmt = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    if (selection.end) return `${fmt(selection.start)} — ${fmt(selection.end)}`
    return `Since ${fmt(selection.start)}`
  }
  return 'All Time'
}
